import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API, authFetch } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Trophy,
  Medal,
  Star,
  ArrowLeft,
  Loader2,
  Gift,
  Users,
  DollarSign,
  Copy,
  Share2,
  TrendingUp,
  Crown
} from "lucide-react";

export function LeaderboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [leaderboard, setLeaderboard] = useState([]);
  const [myStats, setMyStats] = useState(null);
  const [myCode, setMyCode] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [lbRes, codeRes] = await Promise.all([
        authFetch(`${API}/referrals/leaderboard`),
        authFetch(`${API}/referrals/my-code`)
      ]);

      if (lbRes.ok) {
        const data = await lbRes.json();
        setLeaderboard(data.leaderboard || []);
        setMyStats(data.my_stats);
      }
      if (codeRes.ok) {
        const data = await codeRes.json();
        setMyCode(data.referral_code || "");
      }
    } catch (_error) {
      console.error("Error:", _error);
    } finally {
      setLoading(false);
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(myCode);
    toast.success("Referral code copied!");
  };

  const shareLink = () => {
    const url = `${window.location.origin}/register?ref=${myCode}`;
    if (navigator.share) {
      navigator.share({ title: "Join My Biz Consulting", url });
    } else {
      navigator.clipboard.writeText(url);
      toast.success("Referral link copied!");
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-5 h-5 text-yellow-400" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-gray-300" />;
    if (rank === 3) return <Medal className="w-5 h-5 text-amber-600" />;
    return <span className="text-white/60 font-mono">#{rank}</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <Button
            variant="ghost"
            className="text-white/70 hover:text-white"
            onClick={() => navigate("/dashboard")}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Hero */}
        <div className="text-center mb-10">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center mx-auto mb-4">
            <Trophy className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Referral Leaderboard</h1>
          <p className="text-white/60">Top referrers earn badges and rewards</p>
        </div>

        {/* My Stats Card */}
        <Card className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border-purple-500/30 mb-8">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row items-center gap-6">
              {/* Badge */}
              <div className="text-center">
                {myStats?.badge ? (
                  <div className="w-20 h-20 rounded-full flex items-center justify-center text-4xl" 
                       style={{ backgroundColor: myStats.badge.color + "20" }}>
                    {myStats.badge.emoji}
                  </div>
                ) : (
                  <div className="w-20 h-20 rounded-full bg-white/10 flex items-center justify-center">
                    <Star className="w-8 h-8 text-white/40" />
                  </div>
                )}
                <p className="text-sm text-white/60 mt-2">
                  {myStats?.badge?.name || "No badge yet"}
                </p>
              </div>

              {/* Stats */}
              <div className="flex-1 grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-3xl font-bold text-white">{myStats?.total_referrals || 0}</p>
                  <p className="text-sm text-white/60">Successful Referrals</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-green-400">${(myStats?.total_earnings || 0).toFixed(2)}</p>
                  <p className="text-sm text-white/60">Total Earned</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-purple-400">
                    {myStats?.rank ? `#${myStats.rank}` : "-"}
                  </p>
                  <p className="text-sm text-white/60">Your Rank</p>
                </div>
              </div>

              {/* Share Code */}
              <div className="text-center">
                <p className="text-xs text-white/60 mb-2">Your Referral Code</p>
                <div className="flex items-center gap-2">
                  <code className="px-4 py-2 bg-white/10 rounded-lg text-white font-mono">
                    {myCode || "N/A"}
                  </code>
                  <Button size="sm" variant="ghost" onClick={copyCode} className="text-white/70">
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <Button
                  size="sm"
                  className="mt-2 bg-gradient-to-r from-purple-600 to-pink-600"
                  onClick={shareLink}
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share Link
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Leaderboard */}
        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-purple-400" />
              Top Referrers
            </CardTitle>
          </CardHeader>
          <CardContent>
            {leaderboard.length > 0 ? (
              <div className="space-y-3">
                {leaderboard.map((entry, idx) => {
                  const isMe = entry.user_id === user?.user_id;
                  const isTop3 = entry.rank <= 3;
                  
                  return (
                    <div
                      key={entry.user_id}
                      className={`flex items-center gap-4 p-4 rounded-xl transition-all ${
                        isMe ? "bg-purple-500/20 border border-purple-500/30" :
                        isTop3 ? "bg-gradient-to-r from-yellow-500/10 to-orange-500/10" :
                        "bg-white/5 hover:bg-white/10"
                      }`}
                    >
                      {/* Rank */}
                      <div className="w-10 text-center">
                        {getRankIcon(entry.rank)}
                      </div>

                      {/* Avatar */}
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold ${
                        isTop3 ? "bg-gradient-to-br from-yellow-400 to-orange-500 text-white" :
                        "bg-white/10 text-white/70"
                      }`}>
                        {entry.email_initial}
                      </div>

                      {/* Name & Badge */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className={`font-medium ${isMe ? "text-purple-300" : "text-white"}`}>
                            {entry.name}
                            {isMe && <span className="text-xs text-purple-400 ml-2">(You)</span>}
                          </p>
                          {entry.badge && (
                            <span title={entry.badge.name}>{entry.badge.emoji}</span>
                          )}
                        </div>
                        {entry.badge && (
                          <p className="text-xs text-white/50">{entry.badge.name}</p>
                        )}
                      </div>

                      {/* Stats */}
                      <div className="text-right">
                        <p className="text-white font-bold">{entry.total_referrals} referrals</p>
                        <p className="text-sm text-green-400">${entry.total_earnings.toFixed(2)} earned</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-white/20 mx-auto mb-3" />
                <p className="text-white/60">No referrals yet</p>
                <p className="text-sm text-white/40 mt-1">Be the first to refer someone!</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Badges Info */}
        <Card className="bg-white/5 border-white/10 mt-8">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Gift className="w-5 h-5 text-pink-400" />
              Earn Badges
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {[
                { count: 1, emoji: "🏆", name: "Referral Champion", color: "#FFD700" },
                { count: 2, emoji: "🥈", name: "Silver Advocate", color: "#C0C0C0" },
                { count: 3, emoji: "🥉", name: "Bronze Connector", color: "#CD7F32" },
                { count: 5, emoji: "⭐", name: "Rising Star", color: "#9333EA" },
                { count: 10, emoji: "🚀", name: "Super Connector", color: "#EC4899" }
              ].map((badge) => (
                <div
                  key={badge.count}
                  className="text-center p-4 rounded-xl"
                  style={{ backgroundColor: badge.color + "15" }}
                >
                  <div className="text-3xl mb-2">{badge.emoji}</div>
                  <p className="text-white text-sm font-medium">{badge.name}</p>
                  <p className="text-white/50 text-xs mt-1">{badge.count}+ referrals</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

export default LeaderboardPage;
